import React from "react";
import { Container, Section, PageHero, Card, Eyebrow, CTABand } from "@/components/ui-bits/Primitives";
import { FLEET_TARGETS, IMAGES } from "@/lib/site-data";
import { Truck, Gauge, BarChart3, Layers, CheckCircle2 } from "lucide-react";

const BENEFITS = [
    { icon: Gauge, title: "Reduced Fuel Costs", body: "Cut per-mile energy cost by 50-70% vs. diesel and gas equivalents." },
    { icon: BarChart3, title: "Fleet Management Integration", body: "Telematics-ready chargers, scheduled charging, route optimization." },
    { icon: Layers, title: "Charging Analytics", body: "Per-vehicle session history, energy attribution, demand-charge management." },
    { icon: Truck, title: "Scalable Deployment", body: "Modular depot architecture grows as your fleet electrifies — without rebuilds." },
];

export default function Fleet() {
    return (
        <div data-testid="fleet-page">
            <PageHero
                kicker="Fleet Charging"
                title="Depot infrastructure that scales with you."
                lead="From last-mile vans to Class 8 trucks — we engineer fleet charging depots that perform on day one and scale through the decade."
                image={IMAGES.fleet}
            />

            <Section>
                <Container>
                    <Eyebrow className="mb-4">Who We Serve</Eyebrow>
                    <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        {FLEET_TARGETS.map((f) => (
                            <div key={f.label} className="border border-white/10 bg-ink-700/40 p-5 text-center text-xs uppercase tracking-[0.16em] text-slate-300 hover:border-electric-400/50 transition-colors">
                                {f.label}
                            </div>
                        ))}
                    </div>
                </Container>
            </Section>

            <Section className="border-t border-white/5">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <Eyebrow className="mb-4">Fleet Benefits</Eyebrow>
                            <h2 className="font-heading text-3xl md:text-4xl text-white tracking-tight leading-tight">Operational economics that work today.</h2>
                            <p className="mt-6 text-slate-400 leading-relaxed">We design for uptime, energy economics, and TCO — not just hardware specs. Our depot designs incorporate dynamic load management, demand-charge mitigation, and battery storage where the math supports it.</p>
                            <ul className="mt-8 space-y-3">
                                {["DC Fast & megawatt-class depots", "Battery storage & solar integration", "OCPP-based fleet management", "American vendor equipment for federal fleets", "Telematics integration ready"].map((b) => (
                                    <li key={b} className="flex items-center gap-3 text-slate-300 text-sm"><CheckCircle2 className="w-4 h-4 text-electric-400" /> {b}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {BENEFITS.map((b) => {
                                const I = b.icon;
                                return (
                                    <Card key={b.title}>
                                        <I className="w-7 h-7 text-electric-400 mb-5" strokeWidth={1.5} />
                                        <h3 className="font-heading text-lg text-white">{b.title}</h3>
                                        <p className="mt-3 text-sm text-slate-400 leading-relaxed">{b.body}</p>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                </Container>
            </Section>

            <CTABand title="Planning a fleet depot?" subtitle="Tell us your vehicle mix and duty cycle — we'll spec the depot." />
        </div>
    );
}
